/**
 * Bandeau persistant affiché en haut de l'espace compétition pour les
 * visiteurs (= connectés mais sans abonnement compétition actif).
 *
 * Conçu pour être placé en haut des panels concernés (Compétitions,
 * Bibliothèque, etc.) sans bloquer l'usage. L'utilisateur peut le
 * réduire ; l'état est conservé en localStorage pour la session.
 *
 * Si l'utilisateur clique sur « S'abonner », on bascule l'onglet
 * « Abonnement » de Settings — la conversion finale (Stripe Checkout)
 * reste pilotée depuis `Subscription.tsx`, source unique de vérité.
 */

import { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { isNative } from '../platform/capacitor';
import '../styles/competition-upsell.css';

const STORAGE_KEY_MINIMIZED = 'wt_neo_competition_upsell_minimized';

interface CompetitionUpsellBannerProps {
  /** Texte personnalisé pour le contexte (optionnel). */
  message?: string;
  /** Variante visuelle compacte. */
  compact?: boolean;
}

export default function CompetitionUpsellBanner({
  message,
  compact = false,
}: CompetitionUpsellBannerProps) {
  const { setActiveTab } = useApp();
  const native = isNative();
  const [minimized, setMinimized] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_MINIMIZED) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_MINIMIZED, minimized ? '1' : '0');
    } catch {
      // ignore quota / private mode
    }
  }, [minimized]);

  const goSubscribe = () => setActiveTab('subscription');

  if (minimized) {
    return (
      <button
        type="button"
        className="competition-upsell-banner minimized"
        onClick={() => setMinimized(false)}
        title="Voir les détails de l'abonnement compétition"
      >
        <span aria-hidden="true">🏆</span>
        <span>{native ? 'Mode visiteur' : 'Mode visiteur — s\'abonner'}</span>
      </button>
    );
  }

  return (
    <div className={`competition-upsell-banner${compact ? ' compact' : ''}`} role="region" aria-label="Information abonnement compétition">
      <div className="competition-upsell-icon" aria-hidden="true">🏆</div>
      <div className="competition-upsell-text">
        <strong>Vous explorez l'espace compétition en mode visiteur.</strong>
        <span>
          {message ?? 'Certaines fonctions (inscription aux tournois, coach IA, profils détaillés…) nécessitent un abonnement.'}
        </span>
      </div>
      <div className="competition-upsell-actions">
        {native ? (
          <p className="competition-upsell-native-note">
            Abonnement sur webtarot.fr, depuis un navigateur.
          </p>
        ) : (
          <button type="button" className="competition-upsell-primary" onClick={goSubscribe}>
            Voir l'abonnement
          </button>
        )}
        <button
          type="button"
          className="competition-upsell-minimize"
          onClick={() => setMinimized(true)}
          title="Réduire"
          aria-label="Réduire le bandeau"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
